import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { getErrorResponseMessage } from "@via/utils/helpers";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  CreateWorkbenchRequestBody,
  GetWorkbenchAppTemplatesResponseBody,
  ListWorkbenchApplicationResponse,
  ListWorkbenchInstancesResponseBody,
  UpdateWorkbenchApplicationRequestBody,
  UpdateWorkbenchInstanceRequestBody,
} from "common";

export const fetchWorkbenchsList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter: Array<{ id: string; value: unknown }>
): Promise<ListWorkbenchApplicationResponse> => {
  try {
    const baseUrl = "/api/workbench/v1/application/list";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter && filter.length > 0) {
      filter.map(f => {
        queryParams.append(f.id, f.value as string);
      });
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    return await axios.get(url);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useWorkbenchsList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): UseQueryResult<ListWorkbenchApplicationResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/workbench/v1/application/list", take, skip, sort, filter],
    queryFn: () => fetchWorkbenchsList(take, skip, sort, filter),
    retry: false,
  });
};

export const useDeleteWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.delete(`/api/workbench/v1/application/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-deleted",
        message: "Workbench Successfully Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/application/list"],
      });
    },
    onError: error => {
      setNotification({
        id: "workbench-delete-failed",
        message: "Delete Workbench Failed",
        details: "Deleted Workbench can not be recovered",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post("/api/workbench/v1/application", payload);
      return res.data;
    },
    onSuccess: data => {
      setNotification({
        id: "workbench-created",
        message: "Workbench Successfully Created",
        details: "You can now launch this workbench",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the workbench list
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/application/list"],
      });
      return data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-creation-failed",
        message: "Workbench Creation Failed",
        details:
          getErrorResponseMessage(error) ?? "Workbench could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateWorkbench = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: UpdateWorkbenchApplicationRequestBody;
    }) => {
      const res = await axios.patch(
        `/api/workbench/v1/application/${params.id}`,
        params.payload
      );
      return res.data;
    },
    onSuccess: data => {
      setNotification({
        id: "workbench-updated",
        message: "Workbench Successfully Updated",
        details: "You can now launch this workbench",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the workbench list
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/application/list"],
      });
      return data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-update-failed",
        message: "Workbench Update Failed",
        details:
          getErrorResponseMessage(error) ?? "Workbench could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchWorkbenchsInstanceList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: string
): Promise<ListWorkbenchInstancesResponseBody> => {
  try {
    const baseUrl = "/api/workbench/v1/instance/list";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter) {
      queryParams.append("filter", filter);
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useWorkbenchsInstanceList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  { refetchInterval },
  filter?: string
): UseQueryResult<ListWorkbenchInstancesResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/workbench/v1/instance/list", take, skip, sort, filter],
    queryFn: () => fetchWorkbenchsInstanceList(take, skip, sort, filter),
    retry: true,
    refetchOnMount: "always",
    refetchInterval,
  });
};

export const fetchWorkbenchProxyTemplateList = async (): Promise<
  GetWorkbenchAppTemplatesResponseBody[]
> => {
  try {
    const res = await axios.get("/api/workbench/v1/application/templates");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useWorkbenchProxyTemplateList = (): UseQueryResult<
  GetWorkbenchAppTemplatesResponseBody[],
  Error
> => {
  return useQuery({
    queryKey: ["/api/workbench/v1/application/templates"],
    queryFn: async () => await fetchWorkbenchProxyTemplateList(),
    retry: false,
  });
};

export const useLaunchWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (body: {
      workbenchId: number;
      payload: CreateWorkbenchRequestBody;
    }) => {
      const res = await axios.post(
        `/api/workbench/v1/instance/create/${body.workbenchId}`,
        body.payload
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-launching",
        message: "Workbench Launching",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/application/list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-launch-failed",
        message: "Launching Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteWorkbenchInstance = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (workbenchId: number) => {
      const res = await axios.delete(
        `/api/workbench/v1/instance/${workbenchId}`
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-terminating",
        message: "Workbench Terminating",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/application/list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-terminate-failed",
        message: "Terminating Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useStopWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.get(`/api/workbench/v1/instance/stop/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-stopped",
        message: "Workbench pausing in progress...",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-stop-failed",
        message: "Pause Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useStartWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.get(`/api/workbench/v1/instance/start/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-started",
        message: "Workbench is initializing...",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-start-failed",
        message: "Start Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useTerminateWorkbench = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.get(`/api/workbench/v1/instance/terminate/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-terminated",
        message: "Workbench Successfully Terminated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-terminate-failed",
        message: "Terminate Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useSetWorkbenchTimeout = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: { id: number; totalSeconds: number }) => {
      const res = await axios.post(
        `/api/workbench/v1/instance/${params.id}/set-timeout`,
        { totalSeconds: params.totalSeconds }
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-timeout-set",
        message: "Timeout Updated Successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-set-timeout-failed",
        message: "Set Timeout Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateWorkbenchInstance = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      body: UpdateWorkbenchInstanceRequestBody;
    }) => {
      const res = await axios.patch(
        `/api/workbench/v1/instance/${params.id}`,
        params.body
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "workbench-updated",
        message: "Workbench Instance Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/workbench/v1/instance/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "workbench-update-failed",
        message: "Update Workbench Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useFetchWorkbenchPassword = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.get(`/api/workbench/v1/instance/${id}/password`);
      return res.data;
    },
    onError: error => {
      setNotification({
        id: "workbench-password-failed",
        message: "Password Failed",
        details: "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
